import { ArrowLeft, Download } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBillsDetailCustom, getBillsDetailExportExcel } from '../../../api/report.api';
import styles from '../../../assets/scss/pages/ReportSalesDetail.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';

const PAGE_SIZE = 30;

function fmtDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getQuarterRange() {
    const now = new Date();
    const qStart = Math.floor(now.getMonth() / 3) * 3;
    const start = new Date(now.getFullYear(), qStart, 1);
    const end = new Date(now.getFullYear(), qStart + 3, 0);
    return { startDate: fmtDate(start), endDate: fmtDate(end) };
}

function formatDateTime(str) {
    if (!str) return '—';
    const d = new Date(str);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

function formatNum(v) {
    return (v || 0).toLocaleString('vi-VN');
}

export default function ReportBillsDetail() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.REPORT.REVENUE_BILL_DETAIL_VIEW);
    const canExport = hasPermission(PERMISSIONS.REPORT.EXPORT_REVENUE_BILL);
    const [searchParams] = useSearchParams();

    const urlStart = searchParams.get('startDate');
    const urlEnd = searchParams.get('endDate');
    const initQuarter = getQuarterRange();

    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [startDate, setStartDate] = useState(urlStart || initQuarter.startDate);
    const [endDate, setEndDate] = useState(urlEnd || initQuarter.endDate);
    const [billsData, setBillsData] = useState([]);
    const [summary, setSummary] = useState({ totalRevenue: 0, totalCost: 0, totalProfit: 0 });
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 1 });

    const fetchData = useCallback(async (p = page) => {
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        try {
            const { data } = await getBillsDetailCustom({ startDate, endDate, page: p, limit: PAGE_SIZE });

            // API trả về { billsDetail, pagination } khi có page/limit, hoặc array khi không
            const bills = data?.billsDetail || (Array.isArray(data) ? data : []);
            setBillsData(bills);

            // Summary: tính từ toàn bộ (API không trả summary riêng, nhưng pagination có totalItems)
            // Lần đầu fetch page 1 cũng fetch tổng
            if (p === 1) {
                // Fetch không phân trang để lấy tổng
                const { data: allData } = await getBillsDetailCustom({ startDate, endDate });
                const allBills = allData?.billsDetail || (Array.isArray(allData) ? allData : []);
                setSummary({
                    totalRevenue: allBills.reduce((s, b) => s + (b.revenue || 0), 0),
                    totalCost: allBills.reduce((s, b) => s + (b.cost || 0), 0),
                    totalProfit: allBills.reduce((s, b) => s + (b.profit || 0), 0),
                });
            }

            if (data?.pagination) {
                setPagination(data.pagination);
            } else {
                const total = bills.length;
                setPagination({ totalItems: total, totalPages: 1 });
            }
        } catch (err) {
            console.error('Error fetching bills detail:', err);
            setBillsData([]);
            setSummary({ totalRevenue: 0, totalCost: 0, totalProfit: 0 });
        } finally {
            setLoading(false);
        }
    }, [canView, startDate, endDate, page]);

    useEffect(() => {
        fetchData(page);
    }, [fetchData, page]);

    const handleStartDateChange = (e) => {
        const val = e.target.value;
        setStartDate(val);
        if (val > endDate) setEndDate(val);
        setPage(1);
    };

    const handleEndDateChange = (e) => {
        const val = e.target.value;
        setEndDate(val);
        if (val < startDate) setStartDate(val);
        setPage(1);
    };

    // Xuất Excel — không phân trang, xuất hết
    const handleExportExcel = async () => {
        if (!canExport || exporting) return;
        setExporting(true);
        try {
            const res = await getBillsDetailExportExcel({ startDate, endDate });
            const blob = new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ChiTietHoaDon_${startDate}_${endDate}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            alert('Xuất Excel thất bại');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft />
                </button>
                <h1 className={styles.title}>Doanh thu hoá đơn</h1>
            </div>

            {/* Date filters */}
            <div className={styles.filters}>
                <div className={styles.customDateRow}>
                    <div className={styles.dateGroup}>
                        <span className={styles.dateLabel}>Từ ngày:</span>
                        <input type="date" className={styles.dateInput} value={startDate} onChange={handleStartDateChange} />
                    </div>
                    <div className={styles.dateGroup}>
                        <span className={styles.dateLabel}>Đến ngày:</span>
                        <input type="date" className={styles.dateInput} value={endDate} onChange={handleEndDateChange} />
                    </div>
                    {canExport && (
                        <button
                            className={styles.periodTab}
                            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, background: '#22C55E', color: '#fff', borderColor: '#22C55E', borderRadius: 8 }}
                            onClick={handleExportExcel}
                            disabled={exporting}
                        >
                            <Download size={14} />
                            {exporting ? 'Đang xuất...' : 'Xuất Excel'}
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableCard}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} />
                        Đang tải dữ liệu...
                    </div>
                ) : billsData.length === 0 ? (
                    <div className={styles.empty}>Không có dữ liệu trong khoảng thời gian này</div>
                ) : (
                    <>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: 45, textAlign: 'center' }}>STT</th>
                                <th style={{ width: 120 }}>Thời gian</th>
                                <th style={{ width: 180 }}>Khách hàng</th>
                                <th style={{ width: '20%' }}>Doanh thu</th>
                                <th style={{ width: '20%' }}>Giá vốn</th>
                                <th style={{ width: '20%' }}>Lợi nhuận</th>
                            </tr>
                            <tr className={styles.summaryRow}>
                                <td colSpan={3} className={styles.summaryLabel}>Tổng</td>
                                <td className={styles.summaryValue}>{formatNum(summary.totalRevenue)}</td>
                                <td className={styles.summaryValue}>{formatNum(summary.totalCost)}</td>
                                <td className={styles.summaryValue}>{formatNum(summary.totalProfit)}</td>
                            </tr>
                        </thead>
                        <tbody>
                            {billsData.map((bill, idx) => (
                                <tr key={bill.id || idx}>
                                    <td style={{ textAlign: 'center', color: '#6B7280' }}>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                                    <td>{formatDateTime(bill.checkoutTime)}</td>
                                    <td>{bill.customerName || 'Khách lẻ'}</td>
                                    <td>{formatNum(bill.revenue)}</td>
                                    <td>{formatNum(bill.cost)}</td>
                                    <td className={styles.profitCell}>{formatNum(bill.profit)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <span className={styles.paginationInfo}>
                                {`${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, pagination.totalItems)} / ${pagination.totalItems}`}
                            </span>
                            <div className={styles.paginationControls}>
                                <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>
                                    {'‹'}
                                </button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                                    .filter((n) => n === 1 || n === pagination.totalPages || Math.abs(n - page) <= 2)
                                    .reduce((acc, n, i, arr) => {
                                        if (i > 0 && n - arr[i - 1] > 1) acc.push('dots-' + i);
                                        acc.push(n);
                                        return acc;
                                    }, [])
                                    .map((n) =>
                                        typeof n === 'string' ? (
                                            <span key={n} className={styles.pageDots}>...</span>
                                        ) : (
                                            <button
                                                key={n}
                                                className={`${styles.pageBtn}${n === page ? ` ${styles.pageBtnActive}` : ''}`}
                                                onClick={() => setPage(n)}
                                            >
                                                {n}
                                            </button>
                                        ),
                                    )}
                                <button className={styles.pageBtn} disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                                    {'›'}
                                </button>
                            </div>
                        </div>
                    )}
                    </>
                )}
            </div>
        </div>
    );
}
